import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { BillUpdate, BillCommunicationLog } from '../types/bill';
import type { RealtimeChannel } from '@supabase/supabase-js';

/* ── Per-bill realtime (used when modal is open) ── */

/* Minimal shape of a chat_messages row — enough for the v3 CasePage to
   render. The full type lives inline in Dashboard.tsx; we keep this
   permissive so the hook doesn't pin callers to a specific TS type. */
type ChatMessageRow = {
  id: string;
  bill_id: string;
  sender_type: string;
  sender_id?: string | null;
  sender_name: string | null;
  message: string | null;
  attachments: { url: string; name: string; type: string }[] | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  created_at: string;
  read_at: string | null;
  edited_at: string | null;
  /* Admin-only note (migration 035) — the portal must never surface
     or mark these as read. */
  is_internal?: boolean;
  /* Soft-delete from migration 050 — null = visible. */
  deleted_at?: string | null;
  /* Migration 098: 'upload' when the row was written because the
     customer added files on the case page, null when typed. */
  source?: string | null;
};

type UseBillRealtimeOpts = {
  billId: string | null;
  onNewUpdate: (update: BillUpdate) => void;
  onNewCommLog: (log: BillCommunicationLog) => void;
  /* v3 case page subscribes to chat_messages too. Optional so the
     existing BillDetailModal callers don't have to change. */
  onNewChatMessage?: (msg: ChatMessageRow) => void;
  /* Case state v2 (migration 084): customer_requests INSERT/UPDATE and
     bills UPDATE. Without this, a customer sitting on an open case page
     would never see a request ops just raised — the other channels only
     carry timeline rows, comm logs and chat, and creating a request
     writes none of those. They'd get the email and find a page still
     saying "Nothing right now". Optional, same reason as above. */
  onCaseStateChange?: () => void;
};

export function useBillRealtime({
  billId,
  onNewUpdate,
  onNewCommLog,
  onNewChatMessage,
  onCaseStateChange,
}: UseBillRealtimeOpts) {
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!billId) return;

    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    let channel = supabase
      .channel(`bill-detail-${billId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bill_updates',
          filter: `bill_id=eq.${billId}`,
        },
        (payload) => {
          onNewUpdate(payload.new as BillUpdate);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bill_communication_logs',
          filter: `bill_id=eq.${billId}`,
        },
        (payload) => {
          onNewCommLog(payload.new as BillCommunicationLog);
        }
      );

    if (onNewChatMessage) {
      channel = channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `bill_id=eq.${billId}`,
        },
        (payload) => {
          onNewChatMessage(payload.new as ChatMessageRow);
        }
      );
    }

    if (onCaseStateChange) {
      /* Any change to the ask, and any change to the case row itself
         (stage, waiting_on, pause, outcome). '*' rather than INSERT:
         a request being satisfied or reopened matters as much to the
         customer as one being created. */
      channel = channel
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'customer_requests',
            filter: `bill_id=eq.${billId}`,
          },
          () => onCaseStateChange()
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'bills',
            filter: `id=eq.${billId}`,
          },
          () => onCaseStateChange()
        );
    }

    channel.subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [billId, onNewUpdate, onNewCommLog, onNewChatMessage, onCaseStateChange]);
}

export type { ChatMessageRow };

/* ── Unread counts (used on cards/grid) ── */

type UnreadCounts = Record<string, number>; // billId -> unread count

const STORAGE_KEY = 'mediloop_bill_last_seen';

function getLastSeenMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function setLastSeen(billId: string) {
  const map = getLastSeenMap();
  map[billId] = new Date().toISOString();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

export function useUnreadCounts(billIds: string[]) {
  const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({});

  const fetchCounts = useCallback(async () => {
    if (billIds.length === 0) return;

    const lastSeenMap = getLastSeenMap();

    // Seed any bills we haven't seen before so existing data
    // doesn't show as "new". Only newly-arriving rows after this
    // timestamp will trigger unread badges.
    const now = new Date().toISOString();
    let dirty = false;
    for (const id of billIds) {
      if (!lastSeenMap[id]) {
        lastSeenMap[id] = now;
        dirty = true;
      }
    }
    if (dirty) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lastSeenMap));
    }

    // Now count rows newer than each bill's lastSeen timestamp
    const [updatesRes, logsRes, chatRes] = await Promise.all([
      supabase
        .from('bill_updates')
        .select('bill_id, created_at')
        .in('bill_id', billIds)
        .order('created_at', { ascending: false }),
      supabase
        .from('bill_communication_logs')
        .select('bill_id, created_at')
        .in('bill_id', billIds)
        .order('created_at', { ascending: false }),
      // Chat messages use the proper read_at field rather than the
      // lastSeen localStorage trick — more reliable, syncs across
      // devices, doesn't reset when localStorage is cleared.
      supabase
        .from('chat_messages')
        .select('bill_id')
        .in('bill_id', billIds)
        .eq('sender_type', 'admin')
        .is('deleted_at', null)
        .is('read_at', null),
    ]);

    const allRows = [...(updatesRes.data || []), ...(logsRes.data || [])];
    const counts: UnreadCounts = {};

    for (const row of allRows) {
      const lastSeen = lastSeenMap[row.bill_id];
      if (lastSeen && new Date(row.created_at) > new Date(lastSeen)) {
        counts[row.bill_id] = (counts[row.bill_id] || 0) + 1;
      }
    }

    // Layer in unread chat messages on top of update/log counts
    for (const row of chatRes.data || []) {
      counts[row.bill_id] = (counts[row.bill_id] || 0) + 1;
    }

    setUnreadCounts(counts);
  }, [billIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCounts();
    if (billIds.length === 0) return;

    // Realtime: refetch counts whenever a new chat message arrives or any
    // existing one gets read (read_at change). Without this the badge
    // only updates on page reload.
    const channel = supabase
      .channel('chat-unread-counts')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        () => fetchCounts(),
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'chat_messages' },
        () => fetchCounts(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCounts, billIds.join(',')]);

  const markAsRead = useCallback((billId: string) => {
    setLastSeen(billId);
    setUnreadCounts((prev) => {
      const next = { ...prev };
      delete next[billId];
      return next;
    });
  }, []);

  return { unreadCounts, markAsRead, refetchCounts: fetchCounts };
}
